import { FadeUp, AnimatedLine, StaggerContainer, StaggerItem } from "../components/Animations";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

const UKPage = () => {
  const mediaServices = [
    {
      title: "Film & Audio Post-Production",
      image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      description: "Professional motion picture post-production, sound recording, and music publishing services. Supporting filmmakers, content creators, and commercial clients with editing, audio production, and media finishing services for film, television, and digital platforms.",
      services: ["Post-Production", "Sound Recording", "Music Publishing", "Media Finishing"]
    },
    {
      title: "Film Processing & Media Services",
      image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      description: "Specialising in film processing and technical media support services. Providing production support, digital processing, and content preparation services for media and entertainment projects.",
      services: ["Film Processing", "Production Support", "Digital Processing", "Content Preparation"]
    },
    {
      title: "Film Production & Casting",
      image: "https://images.unsplash.com/photo-1440404653325-ab127d49abc1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      description: "Operating across film production, post-production, casting services, and media project support. Providing creative production support for film, television, and commercial media projects.",
      services: ["Film Production", "Post-Production", "Casting Services", "Creative Support"]
    },
  ];

  const otherSectors = [
    {
      title: "Automotive & Technical Services",
      image: "https://images.unsplash.com/photo-1752959827755-350d5933648d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      description: "Specialist automotive-related support services and business solutions within the motor and technical services sector. Delivering operational and technical assistance tailored to automotive and commercial clients.",
      services: ["Automotive Services", "Business Support", "Technical Assistance", "Commercial Solutions"]
    },
    {
      title: "Hospitality & Dining",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      description: "Operating within the hospitality sector, providing café and restaurant services focused on quality dining and customer experience. Delivering food and beverage services in a modern lounge-style environment.",
      services: ["Café Services", "Restaurant Dining", "Lounge Experience", "Quality Hospitality"]
    },
  ];

  return (
    <div data-testid="uk-page">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="container-wide">
          <FadeUp>
            <Link 
              to="/portfolio" 
              className="inline-flex items-center gap-2 text-stone hover:text-charcoal transition-colors duration-300 mb-8"
              data-testid="back-to-portfolio"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-sans uppercase tracking-wider">Global Portfolio</span>
            </Link>
          </FadeUp>

          <FadeUp delay={0.1}>
            <span className="text-caption text-bronze mb-6 block">United Kingdom</span>
            <h1 className="text-display text-charcoal max-w-5xl">
              Media, Automotive & Hospitality
            </h1>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-12 max-w-3xl">
            <p className="text-xl md:text-2xl font-sans font-light text-stone leading-relaxed">
              Our UK portfolio spans media production, automotive services, and hospitality — 
              each business operating independently while contributing to a diversified commercial portfolio.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* UK Overview Stats */}
      <section className="pb-24 md:pb-32">
        <div className="container-wide">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FadeUp>
              <div className="p-8 bg-charcoal text-champagne">
                <span className="text-mono text-champagne/50 block mb-4">Sector</span>
                <h3 className="text-2xl font-serif mb-4">Media & Film</h3>
                <p className="text-sm text-champagne/70">
                  End-to-end film and media production services across multiple specialisations.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="p-8 border-glass">
                <span className="text-mono text-stone/50 block mb-4">Sector</span>
                <h3 className="text-2xl font-serif text-charcoal mb-4">Automotive</h3>
                <p className="text-sm text-stone">
                  Technical services and business support for the motor industry.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="p-8 border-glass">
                <span className="text-mono text-stone/50 block mb-4">Sector</span>
                <h3 className="text-2xl font-serif text-charcoal mb-4">Hospitality</h3>
                <p className="text-sm text-stone">
                  Quality dining experiences in modern lounge environments.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Media, Film & Creative Production */}
      <section className="section-spacing bg-white grain">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Media, Film & Creative Production</span>
            <h2 className="text-headline text-charcoal mb-6">
              Professional media services.
            </h2>
            <p className="text-body text-lg max-w-3xl mb-16">
              Our media portfolio delivers comprehensive film and audio production services, 
              from initial production through post-production and distribution.
            </p>
          </FadeUp>

          <StaggerContainer className="space-y-16" staggerDelay={0.2}>
            {mediaServices.map((service, index) => (
              <StaggerItem key={service.title}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  {/* Image */}
                  <div className={`lg:col-span-5 ${index % 2 === 1 ? 'lg:col-start-8' : ''}`}>
                    <div className="aspect-[4/3] relative overflow-hidden border-glass card-reflect">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-charcoal/10" />
                    </div>
                  </div>

                  {/* Description */}
                  <div className={`lg:col-span-6 ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : 'lg:col-start-7'}`}>
                    <span className="text-mono text-stone/60 block mb-4">0{index + 1}</span>
                    <h3 className="text-2xl font-serif text-charcoal mb-6">{service.title}</h3>
                    <AnimatedLine className="mb-6" />
                    <p className="text-body mb-8">{service.description}</p>
                    <div className="grid grid-cols-2 gap-4">
                      {service.services.map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="w-2 h-2 bg-bronze flex-shrink-0" />
                          <span className="text-sm text-charcoal">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Automotive & Hospitality */}
      <section className="section-spacing">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Automotive & Hospitality</span>
            <h2 className="text-headline text-charcoal mb-16">
              Commercial services & experiences.
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {otherSectors.map((sector, index) => (
              <FadeUp key={sector.title} delay={index * 0.15}>
                <div className="border-glass card-reflect overflow-hidden h-full transition-all duration-500">
                  <div className="aspect-[16/9] relative overflow-hidden">
                    <img 
                      src={sector.image} 
                      alt={sector.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-charcoal/20" />
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-serif text-charcoal mb-4">{sector.title}</h3>
                    <p className="text-body mb-6">{sector.description}</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {sector.services.map((service, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-bronze flex-shrink-0" />
                          <span className="text-sm text-stone">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Group Approach */}
      <section className="section-spacing bg-charcoal">
        <div className="container-wide">
          <div className="max-w-3xl">
            <FadeUp>
              <span className="text-caption text-bronze mb-6 block">Our Approach</span>
              <h2 className="text-headline text-champagne mb-8">
                Independent operation, unified excellence.
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <p className="text-lg font-sans font-light text-champagne/80 mb-6">
                Each UK business operates independently within its specialist sector while 
                contributing to a diversified commercial portfolio delivering professional 
                services across the UK and international markets.
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <p className="text-body text-champagne/60 mb-8">
                This structure allows each operation to maintain focus and expertise in their 
                respective industries while benefiting from the strategic support and 
                resources of the broader group.
              </p>
            </FadeUp>

            <FadeUp delay={0.3}>
              <Link
                to="/philosophy"
                className="inline-flex items-center gap-3 text-champagne hover:text-bronze transition-colors duration-500"
                data-testid="link-to-philosophy"
              >
                <span className="text-sm font-sans font-medium uppercase tracking-wider">Read Our Investment Philosophy</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Cross Links */}
      <section className="section-spacing bg-sand/30">
        <div className="container-wide">
          <FadeUp>
            <h3 className="text-headline text-charcoal mb-12 text-center">
              Explore Our Global Portfolio
            </h3>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FadeUp delay={0.1}>
              <Link 
                to="/portfolio/uae" 
                className="group block border-glass card-reflect shimmer p-8 transition-all duration-500"
                data-testid="link-to-uae"
              >
                <span className="text-mono text-stone/60 mb-2 block">United Arab Emirates</span>
                <h4 className="text-2xl font-serif text-charcoal group-hover:text-bronze transition-colors duration-500 mb-4">
                  Investment & Management
                </h4>
                <p className="text-body text-sm mb-4">Strategic advisory and commercial management services</p>
                <span className="inline-flex items-center gap-2 text-bronze text-sm">
                  Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </FadeUp>
            <FadeUp delay={0.2}>
              <Link 
                to="/portfolio/pakistan" 
                className="group block border-glass card-reflect shimmer p-8 transition-all duration-500"
                data-testid="link-to-pakistan"
              >
                <span className="text-mono text-stone/60 mb-2 block">Pakistan</span>
                <h4 className="text-2xl font-serif text-charcoal group-hover:text-bronze transition-colors duration-500 mb-4">
                  Agriculture & Poultry
                </h4>
                <p className="text-body text-sm mb-4">Poultry farming, breeding, and agricultural infrastructure</p>
                <span className="inline-flex items-center gap-2 text-bronze text-sm">
                  Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 border-t border-sand">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <Link
              to="/portfolio/uae"
              className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
              data-testid="nav-prev-uae"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-sans font-medium uppercase tracking-wider">United Arab Emirates</span>
            </Link>

            <Link
              to="/portfolio/pakistan"
              className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
              data-testid="nav-next-pakistan"
            >
              <span className="text-sm font-sans font-medium uppercase tracking-wider">Pakistan</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UKPage;
